import React, { FC, useRef, useEffect } from "react";
import { useDrop } from "react-dnd";
import Box from "@mui/material/Box";

export interface DnDDroppableBoxProps {
    value: any;
    accept: string[];
    children?: React.ReactNode;
    disabled?: boolean;
    showDropIndicator?: boolean;
    onDragEnter?: () => void;
    onDragLeave?: () => void;
    onDrop?: (draggedItem: any) => void;
}

const DnDDroppableBox: FC<DnDDroppableBoxProps> = ({
                                                       value,
                                                       accept,
                                                       disabled = false,
                                                       showDropIndicator = true,
                                                       onDragEnter,
                                                       onDragLeave,
                                                       onDrop,
                                                       children,
                                                   }) => {
    const ref = useRef<HTMLDivElement>(null);
    const prevIsOverRef = useRef(false);

    const [{ canDrop, isOver }, drop] = useDrop(() => ({
        accept,
        drop: (draggedItem: any, monitor) => {
            // Se è già stato gestito da un child più specifico, non fare nulla
            if (monitor.didDrop()) {
                console.log('⏭️ Drop già gestito da child');
                return;
            }

            // Chiama la callback se presente
            onDrop?.(draggedItem);

            // Ritorna il valore per permettere al parent di gestirlo
            return { value, draggedItem };
        },
        canDrop: () => !disabled,
        collect: (monitor) => ({
            isOver: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
    }), [accept, value, disabled, onDrop]);

    // Gestione eventi drag enter/leave
    useEffect(() => {
        if (isOver && !prevIsOverRef.current) {
            onDragEnter?.();
        } else if (!isOver && prevIsOverRef.current) {
            onDragLeave?.();
        }
        prevIsOverRef.current = isOver;
    }, [isOver, onDragEnter, onDragLeave]);

    // Collega il ref solo se non disabilitato
    if (!disabled && ref.current) {
        drop(ref);
    }

    const isActive = canDrop && isOver;

    return (
        <Box
            ref={ref}
            role="Dustbin"
            sx={{
                position: 'relative',
                ...(showDropIndicator && {
                    border: isActive ? 6 : canDrop && isOver ? 1 : 0,
                    borderColor: isActive ? 'info.main' : 'primary.main',
                    borderStyle: 'dashed',
                    backgroundColor: isActive ? 'action.hover' : 'transparent',
                    minHeight: isActive ? 60: 'inherit',
                }),
                transition: 'all 0.2s ease-in-out',
            }}
        >
            {children}
        </Box>
    );
};

export default DnDDroppableBox;